<template>
  <div class="sensor-chart">
    <header class="title-bar title-bar-between">
      <h3 class="title" v-t="'appSensorChart.trend'" />
      <div class="period-selector-container">
        <span class="label" v-t="'appSensorChart.label.period'" />
        <b-form-select
          size="sm"
          v-bind:options="periodOptions"
          v-model="period"
          class="period-selector"
        />
      </div>
    </header>
    <section class="target-selector-container">
      <target-sensor-item-selector
        v-bind:sensors="sensors"
        v-on:changed="$_setTargetSensorItemIds"
      />
    </section>
    <section class="chart-container">
      <loading-mask v-bind:is-loading="isLoading">
        <component v-bind:is="chartComponent" v-bind="chartProps" />
      </loading-mask>
    </section>
  </div>
</template>

<script>
import moment from 'moment'
import TargetSensorItemSelector from '@/components/sensor/AppTargetSensorItemSelector.vue'
import SensorChartLine from '@/components/sensor/AppSensorChartLine.vue'
import SensorChartBarcode from '@/components/sensor/AppSensorChartBarcode.vue'
import { RestApiClient } from 'ifield-core-components'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import NdjsonClient from '@/lib/NdjsonClient.js'
import { toServerTz } from '@/js/TimezoneHelper'
import { DATETIME_FORMAT_API } from '@/constants.js'

// 期間値定義
const PERIOD = {
  TODAY: 0,
  LAST_ONE_HOUR: 1,
  LAST_THREE_HOURS: 3,
  LAST_SIX_HOURS: 6,
  LAST_TWELVE_HOURS: 12,
}

// チャート種類
const CHART_TYPE = {
  LINE: 'line',
  BARCODE: 'barcode',
}

export default {
  name: 'AppSensorChart',
  components: {
    'target-sensor-item-selector': TargetSensorItemSelector,
  },
  props: {
    /**
     * 選択可能なセンサーID
     */
    sensorIds: {
      type: Array,
      default: null,
    },
    /**
     * プレイスID
     */
    placeId: {
      type: Number,
      default: null,
    },
    /**
     * ユーザーID
     */
    userId: {
      type: Number,
      default: null,
    },
    /**
     * 現在時刻
     */
    currentTime: {
      type: Object,
      default: null,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      // 期間選択肢
      period: PERIOD.TODAY,
      // 対象センサー測定項目ID
      targetSensorItemIds: [],
      // ndjsonクライアント
      clients: new Map(),
      // データ
      data: [],
      // 前回取得時刻
      lastGetDataTime: null,
      // データ取得中フラグ
      isFetching: false,
      // ロード中フラグ
      isLoading: false,
    }
  },
  computed: {
    /**
     * 期間の選択肢
     * @type {Array.<Object>}
     */
    periodOptions: function () {
      return [...Object.values(PERIOD)].map((period) => ({
        value: period,
        text:
          period === PERIOD.TODAY
            ? this.$t('appSensorChart.period.today')
            : this.$t('appSensorChart.period.lastHours', { hour: period }),
      }))
    },
    /**
     * periodに対応するfrom
     * @type {Moment}
     */
    fromOfPeriod: function () {
      if (this.period === PERIOD.TODAY) {
        return moment(this.currentTime).startOf('day')
      } else {
        return moment(this.currentTime).subtract(this.period, 'hours')
      }
    },
    /**
     * periodに対応するto
     * @type {Moment}
     */
    toOfPeriod: function () {
      if (this.period === PERIOD.TODAY) {
        return moment(this.currentTime).endOf('day')
      } else {
        return moment(this.currentTime)
      }
    },
    /**
     * サンプリング間隔(s)
     * @type {string}
     */
    rowUnit: function () {
      switch (this.period) {
        case PERIOD.TODAY:
          return '600'
        case PERIOD.LAST_ONE_HOUR:
          return '10' // 30くらいにしたい
        case PERIOD.LAST_THREE_HOURS:
          return '60'
        case PERIOD.LAST_SIX_HOURS:
          return '60' // 120くらいにしたい
        case PERIOD.LAST_TWELVE_HOURS:
          return '60' // 300くらいにしたい
        default:
          return '60'
      }
    },
    /**
     * 選択できるセンサー
     * @type {Array.<Object>}
     */
    sensors: function () {
      let sensors = []
      if (this.placeId) {
        sensors = this.$store.getters['sensor/getSensorsByPlaceId'](
          this.placeId
        )
      } else if (this.userId) {
        sensors = this.$store.getters['sensor/getSensorsByUserId'](this.userId)
      }

      if (!this.sensorIds) return sensors
      return sensors.filter((s) => this.sensorIds.includes(s.id))
    },
    /**
     * 単位
     * @type {string}
     */
    unit: function () {
      if (!this.targetSensorItemIds.length) return null
      const id = this.targetSensorItemIds[0].sensor_item_id
      const item = this.$store.getters['sensorItem/getSensorItemById'](id)
      return item ? item.unit : null
    },
    /**
     * コード定義
     * @type {Array.<Object>}
     */
    valueCodes: function () {
      if (!this.targetSensorItemIds.length) return null
      const id = this.targetSensorItemIds[0].sensor_item_id
      const item = this.$store.getters['sensorItem/getSensorItemById'](id)
      return item ? item.value_type : []
    },
    /**
     * 描画するチャート種類
     * @type {string}
     */
    chartType: function () {
      return this.valueCodes ? CHART_TYPE.BARCODE : CHART_TYPE.LINE
    },
    /**
     * チャートのコンポーネント
     * @type {VueComponent}
     */
    chartComponent: function () {
      if (!this.targetSensorItemIds.length) return null

      switch (this.chartType) {
        case CHART_TYPE.LINE:
          return SensorChartLine
        case CHART_TYPE.BARCODE:
          return SensorChartBarcode
        default:
          return null
      }
    },
    /**
     * チャートコンポーネントに渡すprops
     * @type {Object}
     */
    chartProps: function () {
      return {
        data: this.data,
        from: this.fromOfPeriod,
        to: this.toOfPeriod,
        unit: this.unit,
        valueCodes: this.valueCodes,
        rowUnit: +this.rowUnit,
      }
    },
  },
  watch: {
    period: function () {
      this.$_getSensorDataAll()
    },
    sensorIds: function (value) {
      if (!value) return
      // 表示対象センサーの中にグラフ表示中のセンサーで含まれていないものがあればグラフを消す
      if (
        this.targetSensorItemIds.some(
          (sensor) => !value.includes(sensor.sensor_id)
        )
      ) {
        this.targetSensorItemIds = []
      }
    },
    targetSensorItemIds: function () {
      this.$_getSensorDataAll()
    },
    currentTime: function () {
      this.$_getSensorDataDiff()
    },
  },
  methods: {
    /**
     * 対象センサー測定項目を設定
     * @param {Object} result 対象センサー測定項目選択結果
     */
    $_setTargetSensorItemIds: function (result) {
      this.targetSensorItemIds = [result.main, ...result.toCompare]
    },

    /**
     * 対象期間すべてのセンサーデータを取得する
     */
    $_getSensorDataAll: async function () {
      // 対象のセンサーが無い場合は何もしない
      if (!this.targetSensorItemIds.length) return
      // 他のリクエストが進行中の時は中断する
      if (this.isFetching) {
        const result = await this.$_abortAll()
        if (!result) {
          this.$log.error('failed to abort preceding request. please reload.')
          return
        }
      }

      this.isFetching = true
      this.isLoading = true
      /**
       * 同じ単位を持つ複数の測定項目が紐付いた製品のセンサーが複数利用できる場合
       * UI上は同単位のものをすべて列挙しているので、センサーと測定項目があべこべな選択ができてしまう
       *
       * ```
       * // sensor_id: 1,2 はどちらとも sensor_item_id: 1,2,3 が紐づく
       * // 以下のような選択が可能
       * [{ sensor_id: 1, sensor_item_id: 3 }, { sensor_id: 2, sensor_item_id: 2 }]
       * ```
       *
       * このときクエリパラメータを `?sensor_id=1,2&senssor_item_id=2,3` とすると
       * 関係ない `[{ sensor_id: 1, sensor_item_id: 2 }, { sensor_id: 2, sensor_item_id: 3 }]` も取得されてしまう。
       *
       * そのため、sensor_id, sensor_item_id の組み合わせをそれぞれ個別にリクエストしてクライアント側で結合する。
       */
      // 各リクエストに渡すパラメータ
      const paramValues = this.targetSensorItemIds.map((item) => ({
        ...item,
        from: this.fromOfPeriod.format(DATETIME_FORMAT_API),
        to: this.currentTime.format(DATETIME_FORMAT_API),
      }))

      try {
        const results = await Promise.all(
          paramValues.map((p) => this.$_getSpecificSensorData(p))
        )
        this.data = results.flat().sort((a, b) => a.sensor_at - b.sensor_at)
      } catch (error) {
        this.handleApiError(error)
        this.isFetching = false
        this.isLoading = false
        return
      }

      if (this.data.length && this.data[0].sensor_at > this.fromOfPeriod) {
        // 先頭データと表示期間の開始の間を埋める
        this.$_fillBlankData()
      }

      this.lastGetDataTime = moment(this.currentTime)
      this.isFetching = false
      this.isLoading = false
    },

    /**
     * 進行中のリクエストをすべて中断する
     * @returns {boolean} 中断成功したらtrue
     */
    $_abortAll: async function () {
      const results = await Promise.allSettled(
        [...this.clients.entries()].map(async ([url, client]) => {
          await client.abort()
          return url
        })
      )

      // abort完了分を client Map から削除
      let isAbortSuccess = true
      for (const result of results) {
        if (result.status === 'fullfilled') {
          this.clients.delete(result.value)
        } else {
          isAbortSuccess = false
        }
      }

      return isAbortSuccess
    },

    /**
     * 前回取得からの差分センサーデータを取得する
     */
    $_getSensorDataDiff: async function () {
      // 対象のセンサーが無い場合は何もしない
      if (!this.targetSensorItemIds.length) return
      // 他のリクエストが進行中の時はスキップ
      if (this.isFetching) return
      // 前回取得が無い場合はスキップ
      if (!this.lastGetDataTime) return

      this.isFetching = true

      /**
       * sensor_id, sensor_item_id の組み合わせごとに個別にリクエストする。
       * 理由は $_getSensorDataAll 内にコメントを参照
       */
      // 各リクエストに渡すパラメータ
      const paramValues = this.targetSensorItemIds.map((item) => ({
        ...item,
        from: this.lastGetDataTime
          .subtract(+this.rowUnit, 'seconds')
          .format(DATETIME_FORMAT_API),
        to: this.currentTime.format(DATETIME_FORMAT_API),
      }))

      let data = []
      try {
        const results = await Promise.all(
          paramValues.map((p) => this.$_getSpecificSensorData(p))
        )
        data = results.flat().sort((a, b) => a.sensor_at - b.sensor_at)
      } catch (error) {
        this.handleApiError(error)
        this.isFetching = false
        return
      }

      this.$_mergeDiffData(data)

      this.lastGetDataTime = moment(this.currentTime)
      this.isFetching = false
    },

    /**
     * 先頭データと表示期間の開始の間を埋める
     */
    $_fillBlankData: function () {
      if (!this.data.length) return

      const { sensor_at, sensor_id, sensor_item_id } = this.data[0]
      const fillData = []
      const firstDate = moment(sensor_at)
      let time = this.fromOfPeriod
      while (time <= firstDate) {
        fillData.push({
          sensor_at: time,
          sensor_id,
          sensor_item_id,
          value: null,
        })
        time += this.rowUnit * 1000
      }
      this.data = [...fillData, ...this.data]
    },

    /**
     * 各センサー測定項目のセンサー値を取得する
     * @param {Object} paramValue クエリパラメータ値
     * @returns {Array.<Object>} 取得結果
     */
    $_getSpecificSensorData: async function (paramValue) {
      const url = RestApiClient.createApiUrl('sensor_data/ndjson')
      this.$_addQueryParameter(url.searchParams, paramValue)

      const client = new NdjsonClient({ storeCache: false })
      // 別トリガー時のabort用に保持
      this.clients.set(url.toString(), client)

      const data = []
      try {
        for await (let values of client.getData(url)) {
          data.push(...values)
        }
      } catch {
        await client.abort()
      } finally {
        this.clients.delete(url.toString())
      }

      return data
    },

    /**
     * リクエストURLにパラメータを付与する
     * @param {URLSearchParams} params
     * @param {Object} paramValue パラメータとして設定する値
     */
    $_addQueryParameter: function (params, paramValue) {
      const { from, to, sensor_id, sensor_item_id } = paramValue

      // 取得期間(必須)
      from && params.append('from', toServerTz(from))
      to && params.append('to', toServerTz(to))

      // センサー指定
      sensor_id && params.append('sensor_id', sensor_id)
      sensor_item_id && params.append('sensor_item_id', sensor_item_id)

      // サンプリング設定
      params.append('row_unit', this.rowUnit)
      params.append('agg_method', 'first')
    },

    /**
     * 取得した差分データをマージする
     */
    $_mergeDiffData: function (diffData) {
      // 重複データのindex取得
      const duplicateIndexes = []
      for (let i = diffData.length - 1; i >= 0; --i) {
        const diffD = diffData[i]
        for (let j = this.data.length - 1; j >= 0; --j) {
          const d = this.data[j]
          if (
            d.sensor_at === diffD.sensor_at &&
            d.sensor_id === diffD.sensor_id &&
            d.sensor_item_id === diffD.sensor_item_id
          ) {
            duplicateIndexes.push(i)
            break
          }
        }
      }

      // 追加
      const hasAdditionalData = duplicateIndexes.length < diffData.length
      if (hasAdditionalData) {
        this.data = [
          ...this.data,
          ...diffData.filter((_, index) => !duplicateIndexes.includes(index)),
        ]
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../assets/scss/parts/title-bar';

.sensor-chart {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 12px;
}

.title-bar {
  height: auto;
  border: none;

  .title {
    font-size: $font-size-middle;
    font-weight: $font-weight-bold;
    margin: 0;
  }
}

.period-selector-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  column-gap: 8px;

  .label {
    font-size: $font-size-small;
    font-weight: $font-weight-bold;
  }

  .period-selector {
    width: auto;
    height: 1.875rem; // 30px
    font-size: $font-size-small;
  }
}

.target-selector-container {
  width: 100%;
}

.chart-container {
  position: relative;
  width: 100%;

  .chart {
    width: 100%;
  }
}
</style>
